import React from "react";
import { createRoot } from "react-dom/client";
import { Controller } from "@hotwired/stimulus";
import { QueryClientProvider } from "react-query";
import queryClient from "../../../helpers/queryClient";
import BatchRenewInviteModal from "../../../components/administration/invites/BatchRenewInviteModal";
import { fetchApi } from "../../../helpers/reactQueryApi";
import { visit } from "@hotwired/turbo";
import bridge from "../../../turbo/bridge";

// Connects to data-controller="administration--invites--send-batch-actions"
export default class extends Controller {
  connect() {
    this.childElement = document.createElement("div");
    this.element.appendChild(this.childElement);
    this.root = createRoot(this.childElement);
  }

  onCloseModal() {
    this.root.render(React.createElement(React.Fragment, null));
  }

  refreshTable() {
    const url = this.getUrlForPage();
    visit(url.toString(), {
      frame: "external-invites-view",
      action: "replace",
    });
  }

  refreshInvites() {
    const invite_ids = this.#getSelectedInviteIds();

    if (invite_ids.length > 1) {
      this.renewInvites(invite_ids);
    } else {
      this.root.render(
        <QueryClientProvider client={queryClient}>
          <BatchRenewInviteModal
            inviteId={invite_ids[0]}
            inviteIds={invite_ids}
            setShowModal={(show) => {
              if (!show) this.onCloseModal();
            }}
            onSuccess={() => this.refreshTable()}
          />
        </QueryClientProvider>,
      );
    }
  }

  renewInvites(invite_ids) {
    const { externalInvitesCount, groupInvitesCount } =
      this.countSelectedInviteTypes();

    bridge.confirm(
      I18n.t("js.invites.batch_renew.confirm", {
        group_invites_count: groupInvitesCount,
        network_invites_count: externalInvitesCount,
      }),
      () => {
        fetchApi("/batch_invites/renew", {
          method: "POST",
          body: {
            body: "",
            invite_ids: invite_ids,
          },
        })
          .then(() => this.batchRenewSuccess())
          .catch(() => this.batchRenewFailure());
      },
    );
  }

  revokeInvites() {
    fetchApi("/batch_invites/revoke", {
      method: "POST",
      body: {
        invite_ids: this.#getSelectedInviteIds(),
      },
    })
      .then(() => this.#submitSuccess())
      .catch(() => this.#submitFailure());
  }

  #getSelectedInviteIds() {
    const selectedCheckboxes = this.element.querySelectorAll(
      'input[name="invite_select"]:checked',
    );
    return Array.from(selectedCheckboxes).map(
      (checkbox) => checkbox.id.split("-")[1],
    );
  }

  countSelectedInviteTypes() {
    let externalInvitesCount = 0;
    let groupInvitesCount = 0;

    const selectedCheckboxes = this.element.querySelectorAll(
      'input[name="invite_select"]:checked',
    );

    Array.from(selectedCheckboxes).forEach((checkbox) => {
      if (checkbox.dataset.type === "ExternalInvite") {
        externalInvitesCount += 1;
      } else {
        groupInvitesCount += 1;
      }
    });

    return { externalInvitesCount, groupInvitesCount };
  }

  #submitSuccess() {
    toastr.success(I18n.t("js.invites.batch_revoke.success_flash"));
    this.refreshTable();
  }

  #submitFailure() {
    toastr.error(I18n.t("js.invites.batch_revoke.not_revokable_flash"));
  }

  batchRenewSuccess() {
    toastr.success(I18n.t("js.invites.batch_renew.success_flash"));
    this.refreshTable();
  }

  batchRenewFailure() {
    toastr.error(I18n.t("js.invites.batch_renew.not_renewable_flash"));
  }

  getUrlForPage() {
    const pageField = document.querySelector("input[name='page']");
    const page = pageField ? pageField.value : "1";

    const url = new URL(location.pathname, window.location.origin);
    url.searchParams.set("page", page);

    return url;
  }

  disconnect() {
    this.root.unmount();
    this.childElement?.remove();
  }
}
